import { LinePath } from '@visx/shape';
import type { ScaleLinear, ScaleTime } from 'd3-scale';

import { useTheme } from '@simplywallst/ui-core';

import type { ValuationType } from './MarketPerformanceChart';
import {
  verticalPaddingLarge,
  verticalPaddingSmall,
  peChartHeight,
} from './MarketPerformanceChart';

import { VisxText } from '@/components/VisxText';

import useTitleGenerator from '@/pages/Market/hooks/useTitleGenerator';
import type { PeriodType } from '@/pages/Market/query/fetchMarketPerformance';
import { formatterFactor } from '@/pages/Market/utils/formatter';

interface Props {
  width: number;
  keys: number[];
  index: number;
  color: string;
  goodColor: string;
  badColor: string;
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  getValueByTime: (time: number, key: string) => number;
  mean: number;
  period: PeriodType;
  valuationType: ValuationType;
  valuationLabel: string;
}

const toolTipWidth = 110;
const toolTipHeight = 19;
const borderRadius = 5;

export const PEChart = ({
  width,
  keys,
  color,
  xScale,
  yScale,
  getValueByTime,
  goodColor,
  badColor,
  mean,
  index,
  period,
  valuationType,
  valuationLabel,
}: Props) => {
  const theme = useTheme();
  const x = xScale(keys[index]) || 0;
  const tooltipAlignLeft = x > width / 2;
  const chartTitle = useTitleGenerator({
    marketTemplate: `[Market] Market ${valuationLabel}`,
    sectorTemplate: `[Market] [Sector] Sector ${valuationLabel}`,
    industryTemplate: `[Market] [Industry] Industry ${valuationLabel}`,
  });
  const padding = width > 480 ? verticalPaddingLarge : verticalPaddingSmall;
  const meanHeight = yScale(mean);
  const badAreaHeight = meanHeight || 0;
  const goodAreaHeight = peChartHeight - badAreaHeight;
  const rectWidth = width >= 60 ? width - padding * 2 : 0;
  const belowArcWidth = width >= 22 ? width - (borderRadius + padding) * 2 : 0;
  return (
    <g pointerEvents="none">
      <linearGradient id="badGradient" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor={badColor} stopOpacity="60%" />
        <stop offset="80%" stopColor={badColor} stopOpacity="20%" />
      </linearGradient>
      <linearGradient id="goodGradient" x1="0" x2="0" y1="0" y2="1">
        <stop offset="10%" stopColor={goodColor} stopOpacity="10%" />
        <stop offset="80%" stopColor={goodColor} stopOpacity="30%" />
      </linearGradient>
      <VisxText
        x={padding + 16}
        y={16}
        fill={theme.color.white}
        typography="tiny"
      >
        {chartTitle}
      </VisxText>
      <path
        d={`M${padding},${borderRadius} Q${padding} 0 ${
          borderRadius + padding
        } 0 H${rectWidth + padding - borderRadius} Q${rectWidth + padding} 0 ${
          rectWidth + padding
        } 5 V${badAreaHeight} H${padding} z`}
        fill="url(#badGradient)"
      />
      <line
        x1={padding}
        y1={badAreaHeight}
        x2={rectWidth + padding}
        y2={badAreaHeight}
        stroke={color}
      />
      <path
        d={`M${padding},${badAreaHeight} h${rectWidth} v${goodAreaHeight} q0,${borderRadius} -${borderRadius},${borderRadius} h-${belowArcWidth} q-${borderRadius},0 -${borderRadius},-${borderRadius} z`}
        fill="url(#goodGradient)"
      />
      <LinePath
        data={keys}
        stroke="#FFF"
        strokeWidth={2}
        strokeLinecap="round"
        x={(d) => xScale(d || 0) as number}
        y={(d) => yScale(getValueByTime(d, valuationType) || 0) as number}
      />
      <rect
        x={tooltipAlignLeft ? x - toolTipWidth : x}
        y={peChartHeight - toolTipHeight + 1}
        height={toolTipHeight}
        width={toolTipWidth}
        rx={borderRadius}
        fill={color}
      />
      <VisxText
        x={
          tooltipAlignLeft ? x + borderRadius - toolTipWidth : x + borderRadius
        }
        y={peChartHeight - borderRadius}
        fill={theme.color.spaceGray}
        typography="tiny"
        height={toolTipHeight}
        width={toolTipWidth}
        scaleToFit={false}
      >
        {`${period} Average ${formatterFactor(mean)}`}
      </VisxText>
      <polygon
        points={`${x},${badAreaHeight} ${x},${peChartHeight - 15} ${
          x + (tooltipAlignLeft ? -10 : 10)
        },${peChartHeight - 15}`}
        fill={color}
      />
    </g>
  );
};
